import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [colors, setColors] = useState({
    primary: '#FFD700',
    secondary: '#1F2937',
    background: '#111827'
  });

  // Update colors when user or organization data changes
  useEffect(() => {
    if (user?.organization) {
      setColors({
        primary: user.organization.primary_color || '#FFD700',
        secondary: user.organization.secondary_color || '#1F2937',
        background: user.organization.background_color || '#111827'
      });
    }
  }, [user]);

  // Apply CSS custom properties to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    
    // Create hover variants
    const primaryRgb = hexToRgb(colors.primary);
    if (primaryRgb) {
      const darkerPrimary = `rgb(${Math.max(0, primaryRgb.r - 30)}, ${Math.max(0, primaryRgb.g - 30)}, ${Math.max(0, primaryRgb.b - 30)})`;
      root.style.setProperty('--color-primary-hover', darkerPrimary);
    }
  }, [colors]);

  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const value = {
    colors,
    setColors,
    // Helper functions for components
    primaryStyle: {
      backgroundColor: colors.primary,
      color: isLightColor(colors.primary) ? '#000000' : '#FFFFFF'
    },
    primaryHoverStyle: {
      backgroundColor: adjustBrightness(colors.primary, -0.1),
      color: isLightColor(colors.primary) ? '#000000' : '#FFFFFF'
    },
    primaryTextStyle: {
      color: colors.primary
    }
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper functions
const isLightColor = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return false;
  
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

const adjustBrightness = (hex, amount) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  
  r = Math.max(0, Math.min(255, r + (amount * 255)));
  g = Math.max(0, Math.min(255, g + (amount * 255)));
  b = Math.max(0, Math.min(255, b + (amount * 255)));
  
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

export default ThemeContext;